import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Aviso,
  Botao,
  EstadoErro,
  ValorFiscal,
  EstadoVazio,
  MenuDeAcoes,
  Selo,
  Tabela,
  useDialogo,
  type ColunaTabela,
  type TomSemantico,
} from '../componentes/ui';
import { AvisoDoLote, BotaoDoLote, useAcaoEmLote } from '../componentes/AcaoEmLote';
import { BotaoExtrairXlsx } from '../componentes/BotaoExtrairXlsx';
import { requisicaoAutenticada } from '../servicos/http';
import { resumirNomes } from '../servicos/lote';
import { formatarCnpj, formatarCpf } from '../servicos/formato';
import { diaLocal, type ColunaDaPlanilha } from '../servicos/planilha';
import { formatarTelefone } from './vinculos-trabalhistas';
import { Pagina } from './Carteira';
import { AnaliseDoPreCadastro, type PreCadastro } from './AnaliseDoPreCadastro';
import { ErroDaApi } from './ErroDaApi';

/**
 * Funil de captação: pré-cadastros do formulário público.
 *
 * ## Duas telas, uma fonte
 *
 * No Deca 1 são duas entradas de menu — "Simulações de Crédito" e "Em
 * Aprovação" — e as duas leem a mesma tabela. A diferença é o `step` do wizard:
 * quem parou no meio deixou uma simulação; quem chegou ao fim (`step === 5`)
 * está na fila de aprovação.
 *
 * Manter as duas entradas é deliberado, porque respondem a perguntas de pessoas
 * diferentes: comercial olha simulações atrás de lead, operação olha aprovação
 * atrás de trabalho. O que muda entre elas é o filtro e as colunas — o resto é o
 * mesmo componente.
 */

export type ModoDeCaptacao = 'simulacoes' | 'aprovacao';

const CONFIG: Readonly<
  Record<ModoDeCaptacao, { titulo: string; descricao: string; vazio: string }>
> = {
  simulacoes: {
    titulo: 'Simulações de crédito',
    descricao:
      'Quem usou a calculadora pública e deixou contato. Cada linha é um lead com um número já calculado.',
    vazio: 'Nenhuma simulação registrada pelo formulário público.',
  },
  aprovacao: {
    titulo: 'Em aprovação',
    descricao:
      'Pré-cadastros que completaram o cadastro e aguardam análise — quem simulou marcando que gerencia uma carteira de empresas chega aqui como assessor. Aprovar provisiona empresa, pessoa e conta de acesso.',
    vazio: 'Nenhum pré-cadastro aguardando análise.',
  },
};

/** O wizard tem cinco etapas; só a última entra na fila de aprovação (RN-321). */
const ETAPA_FINAL = 5;

export function Captacao(props: { readonly modo: ModoDeCaptacao }): JSX.Element {
  const config = CONFIG[props.modo];
  const [analisando, setAnalisando] = useState<PreCadastro | null>(null);
  const navegar = useNavigate();
  const dialogo = useDialogo();
  const queryClient = useQueryClient();

  // Item 7 de 15/09 — exclusão lógica do pré-cadastro (`DELETE /register/:id`).
  const excluir = useMutation({
    mutationFn: (id: string) =>
      requisicaoAutenticada<void>(`/register/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['pre-cadastros'] }),
  });
  // A mesma exclusão, para as linhas marcadas.
  const excluirMarcados = useAcaoEmLote<PreCadastro>({
    acao: (r) => requisicaoAutenticada<void>(`/register/${r.id}`, { metodo: 'DELETE' }),
    nome: nomeDoLead,
    aoTerminar: () => void queryClient.invalidateQueries({ queryKey: ['pre-cadastros'] }),
  });

  const consulta = useQuery({
    queryKey: ['pre-cadastros'],
    queryFn: () => requisicaoAutenticada<PreCadastro[]>('/register?all=true'),
  });

  const todos = consulta.data ?? [];
  const itens =
    props.modo === 'aprovacao'
      ? todos.filter((r) => r.step >= ETAPA_FINAL && !r.aprovado && !r.rejeitado)
      : todos;

  const colunas: readonly ColunaTabela<PreCadastro>[] = [
    {
      chave: 'pessoa',
      titulo: 'Contato',
      larguraMinima: '15rem',
      celula: (r) => (
        <span className="flex flex-col">
          <span className="text-conteudo-primario">{r.personFullName ?? '—'}</span>
          <span className="text-xs text-conteudo-sutil">{r.personEmailAddress ?? ''}</span>
        </span>
      ),
    },
    {
      /*
        Perfil e vínculo, lado a lado: quem trabalha a fila precisa saber se
        vai aprovar um assessor (que vira parceiro) ou um contribuinte, e se
        liga para o sócio ou para o contador.
      */
      chave: 'perfil',
      titulo: 'Perfil',
      larguraMinima: '9rem',
      celula: (r) => (
        <span className="flex flex-col items-start gap-1">
          <Selo tom={r.perfilAssessor === true ? 'progresso' : 'neutro'}>
            {r.perfilAssessor === true ? 'Assessor' : 'Contribuinte'}
          </Selo>
          {r.personOccupation != null && r.personOccupation !== '' && (
            <span className="text-xs text-conteudo-sutil">{r.personOccupation}</span>
          )}
        </span>
      ),
    },
    {
      chave: 'empresa',
      titulo: 'Empresa',
      larguraMinima: '16rem',
      celula: (r) => r.legalName ?? <span className="text-conteudo-sutil">—</span>,
    },
    {
      chave: 'taxpayerId',
      titulo: 'CNPJ',
      larguraMinima: '11rem',
      celula: (r) =>
        r.taxpayerId === null ? (
          <span className="text-conteudo-sutil">—</span>
        ) : (
          <span className="valor-fiscal whitespace-nowrap text-sm">
            {formatarCnpj(r.taxpayerId)}
          </span>
        ),
    },
    ...(props.modo === 'simulacoes'
      ? [
          /*
            O que a tela de simulações existe para mostrar.
            Ela listava nome e empresa e **nenhum número** — o lead que simulou
            R$ 400 mil ficava indistinguível de quem abriu a calculadora e
            desistiu. Há 151 simulações e 50 leads na base; o valor é o que
            ordena a fila comercial.
          */
          {
            chave: 'simulacao',
            titulo: 'Simulou',
            numerica: true,
            larguraMinima: '13rem',
            celula: (r: PreCadastro) =>
              r.simulacao == null ? (
                <span className="text-xs text-conteudo-sutil">Não simulou</span>
              ) : (
                <span className="flex flex-col items-end">
                  <ValorFiscal valor={r.simulacao.beneficioTotal} variante="economia" />
                  <span className="text-xs text-conteudo-sutil">
                    {r.simulacao.meses} {r.simulacao.meses === 1 ? 'mês' : 'meses'}
                    {r.simulacao.anos.length > 0 && ` · ${r.simulacao.anos.join(', ')}`}
                  </span>
                </span>
              ),
          },
          {
            chave: 'step',
            titulo: 'Etapa',
            larguraMinima: '10rem',
            celula: (r: PreCadastro) => <Selo tom={tomDaEtapa(r)}>{rotuloDaEtapa(r)}</Selo>,
          },
        ]
      : []),
    {
      chave: 'cidade',
      titulo: 'Cidade',
      larguraMinima: '11rem',
      celula: (r) =>
        r.addressCity === null ? (
          <span className="text-conteudo-sutil">—</span>
        ) : (
          `${r.addressCity}${r.addressStateCode === null ? '' : `/${r.addressStateCode}`}`
        ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '9rem',
      // A análise abre para qualquer linha, inclusive no modo simulações: um
      // lead que já preencheu tudo pode ser aprovado de lá, e obrigar a trocar
      // de tela para isso seria só cerimônia.
      celula: (r) => (
        <span className="flex items-center justify-end gap-2">
          {r.aprovado ? (
            <Selo tom="positivo">Aprovado</Selo>
          ) : r.rejeitado ? (
            <Selo tom="critico">Rejeitado</Selo>
          ) : (
            <Botao
              variante="secundario"
              tamanho="pequeno"
              onClick={(e) => {
                // A linha também é clicável: sem isto, abria a análise e a
                // simulação ao mesmo tempo, e a navegação levava a tela embora.
                e.stopPropagation();
                setAnalisando(r);
              }}
            >
              Analisar
            </Botao>
          )}
          <MenuDeAcoes
            rotulo={r.personFullName ?? r.personEmailAddress ?? 'pré-cadastro'}
            itens={[
              ...(props.modo === 'simulacoes'
                ? [{ rotulo: 'Ver simulação', aoEscolher: () => navegar(`/simulacoes/${r.id}`) }]
                : []),
              { rotulo: 'Analisar', aoEscolher: () => setAnalisando(r) },
              {
                rotulo: 'Excluir',
                perigo: true,
                descricao: r.aprovado
                  ? 'O cadastro aprovado continua; some só daqui.'
                  : 'Some da captação. As simulações ficam.',
                aoEscolher: () => {
                  void (async () => {
                    const ok = await dialogo.confirmar({
                      titulo: `Excluir o pré-cadastro de ${r.personFullName ?? r.personEmailAddress ?? 'este lead'}?`,
                      descricao:
                        'Ele sai das listas de simulações e de aprovação. Se a pessoa simular de novo com o mesmo e-mail, nasce um pré-cadastro novo.',
                      rotuloConfirmar: 'Excluir',
                      tom: 'perigo',
                    });
                    if (ok) excluir.mutate(r.id);
                  })();
                },
              },
            ]}
          />
        </span>
      ),
    },
  ];

  if (consulta.isError) {
    return (
      <Pagina titulo={config.titulo}>
        <EstadoErro
          titulo="Não foi possível carregar"
          mensagem={consulta.error.message}
          aoTentarNovamente={() => void consulta.refetch()}
        />
      </Pagina>
    );
  }

  return (
    <Pagina
      titulo={config.titulo}
      descricao={config.descricao}
      contagem={itens.length}
      acao={
        <BotaoExtrairXlsx
          titulo={config.titulo}
          disabled={consulta.data === undefined}
          colunas={colunasDaPlanilha(props.modo)}
          linhas={itens}
        />
      }
    >
      {props.modo === 'aprovacao' && itens.length === 0 && todos.length > 0 && (
        <Aviso tom="informacao" titulo="Fila vazia">
          Há {todos.length} pré-cadastro(s) no funil, mas nenhum finalizou o cadastro. Um lead da
          calculadora só entra aqui depois de clicar em "Finalizar meu cadastro" e concluir as
          etapas — até lá, ele aparece em <strong>Simulações de crédito</strong>.
        </Aviso>
      )}

      {excluir.isError && <ErroDaApi titulo="Não foi possível excluir" erro={excluir.error} />}
      <AvisoDoLote
        desfecho={excluirMarcados.desfecho}
        feitos={(n) => (n === 1 ? '1 pré-cadastro excluído' : `${n} pré-cadastros excluídos`)}
        aoFechar={excluirMarcados.limparDesfecho}
      />

      {!consulta.isLoading && itens.length === 0 ? (
        <EstadoVazio titulo="Nada por aqui" descricao={config.vazio} />
      ) : (
        <Tabela
          legenda={config.titulo}
          colunas={colunas}
          dados={itens}
          chaveDaLinha={(r) => r.id}
          chaveDeCabecalhoDaLinha="pessoa"
          carregando={consulta.isLoading}
          // Em simulações, a linha abre o cálculo que o lead fez; na fila de
          // aprovação, a análise do cadastro — é o que cada tela está buscando.
          aoClicarNaLinha={(r) =>
            props.modo === 'simulacoes' ? navegar(`/simulacoes/${r.id}`) : setAnalisando(r)
          }
          selecao={{
            substantivo: { singular: 'pré-cadastro', plural: 'pré-cadastros' },
            nomeDaLinha: nomeDoLead,
            acoes: (marcados, limpar) => (
              <BotaoDoLote
                rotulo="Excluir"
                perigo
                marcadas={marcados}
                limpar={limpar}
                lote={excluirMarcados}
                confirmacao={(m) => ({
                  titulo:
                    m.length === 1
                      ? `Excluir o pré-cadastro de ${nomeDoLead(m[0]!)}?`
                      : `Excluir ${m.length} pré-cadastros?`,
                  descricao: `${resumirNomes(m.map(nomeDoLead))}.\n\nSaem das listas de simulações e de aprovação; o cadastro já aprovado continua. Se a pessoa simular de novo com o mesmo e-mail, nasce um pré-cadastro novo.`,
                  rotuloConfirmar: 'Excluir',
                  tom: 'perigo',
                })}
              />
            ),
          }}
        />
      )}

      <AnaliseDoPreCadastro registro={analisando} aoFechar={() => setAnalisando(null)} />
    </Pagina>
  );
}

/** Quem é o lead, para a seleção e as confirmações: o nome, o e-mail ou a empresa. */
function nomeDoLead(r: PreCadastro): string {
  return r.personFullName ?? r.personEmailAddress ?? r.legalName ?? 'Lead sem contato';
}

/** Onde o lead parou no funil — a lista e a simulação aberta dizem o mesmo. */
/** As colunas da tabela em valores de planilha, com o contato completo de quem preencheu. */
function colunasDaPlanilha(modo: ModoDeCaptacao): ColunaDaPlanilha<PreCadastro>[] {
  return [
    { titulo: 'Contato', largura: 30, valor: (r) => r.personFullName },
    { titulo: 'E-mail', largura: 32, valor: (r) => r.personEmailAddress },
    { titulo: 'Telefone', largura: 16, valor: (r) => formatarTelefone(r.personPhone) },
    {
      titulo: 'CPF',
      largura: 16,
      valor: (r) => (r.personTaxpayerId === null ? null : formatarCpf(r.personTaxpayerId)),
    },
    {
      titulo: 'Perfil',
      largura: 14,
      valor: (r) => (r.perfilAssessor === true ? 'Assessor' : 'Contribuinte'),
    },
    { titulo: 'Vínculo', largura: 20, valor: (r) => r.personOccupation },
    { titulo: 'Empresa', largura: 40, valor: (r) => r.legalName },
    {
      titulo: 'CNPJ',
      largura: 20,
      valor: (r) => (r.taxpayerId === null ? null : formatarCnpj(r.taxpayerId)),
    },
    ...(modo === 'simulacoes'
      ? ([
          {
            titulo: 'Simulou',
            formato: 'moeda',
            valor: (r) => r.simulacao?.beneficioTotal ?? null,
          },
          { titulo: 'Meses simulados', valor: (r) => r.simulacao?.meses ?? null },
          { titulo: 'Anos', largura: 16, valor: (r) => r.simulacao?.anos.join(', ') ?? null },
          { titulo: 'Etapa', largura: 16, valor: (r) => rotuloDaEtapa(r) },
        ] satisfies ColunaDaPlanilha<PreCadastro>[])
      : []),
    { titulo: 'Cidade', largura: 22, valor: (r) => r.addressCity },
    { titulo: 'UF', largura: 6, valor: (r) => r.addressStateCode },
    { titulo: 'Criado em', formato: 'data', valor: (r) => diaLocal(r.audCreatedAt) },
  ];
}

export function rotuloDaEtapa(r: PreCadastro): string {
  if (r.aprovado) return 'Aprovado';
  if (r.step >= ETAPA_FINAL) return 'Concluído';
  if (r.step <= 1) return 'Só simulou';
  return 'Cadastro incompleto';
}

export function tomDaEtapa(r: PreCadastro): TomSemantico {
  if (r.aprovado) return 'positivo';
  if (r.rejeitado) return 'critico';
  if (r.step >= ETAPA_FINAL) return 'progresso';
  return 'neutro';
}
